import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocCheckItem,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  listOrPrompt,
  lowerFirstWord,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A SMART goal: the five criteria answered one by one, assembled into a single goal
 * statement ("By <date>, I will <action>, measured by <measure>, because <reason>.") and
 * checked off, with the writer's first steps as an action plan.
 *
 * The acronym first appears in print in George T. Doran's 1981 *Management Review*
 * article, where it stood for specific, measurable, assignable, realistic and
 * time-related; the achievable / relevant / time-bound reading used here is the one most
 * study guides now teach.
 */

export const SMART_CRITERIA = [
  "specific",
  "measurable",
  "achievable",
  "relevant",
  "timeBound",
] as const;

export type SmartCriterion = (typeof SMART_CRITERIA)[number];

export const CHECK_INS = ["weekly", "biweekly", "monthly"] as const;

export type CheckIn = (typeof CHECK_INS)[number];

export interface SmartGoalInput {
  goal: string;
  specific: string;
  measurable: string;
  achievable: string;
  relevant: string;
  /** An ISO date from a date input: "2026-12-11". */
  deadline: string;
  checkIn: CheckIn;
  /** One step per line. */
  steps: string;
}

export interface SmartGoalLabels {
  defaultTitle: string;
  startingPoint: string;
  headings: { statement: string; checklist: string; plan: string };
  statement: { by: string; will: string; measured: string; because: string };
  statementPrompt: string;
  statementNote: string;
  criteria: Record<SmartCriterion, string>;
  missing: Record<SmartCriterion, string>;
  stepsPrompt: string;
  checkIn: string;
  frequencies: Record<CheckIn, string>;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const END_PUNCTUATION = /[\s.;,:]+$/;

/** "2026-12-11" → "December 11, 2026" in the reader's locale; null if not a real date. */
export function formatDeadline(iso: string, locale: string): string | null {
  const match = ISO_DATE.exec(iso.trim());
  if (!match) {
    return null;
  }
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeZone: "UTC",
  }).format(date);
}

/** A fragment to splice mid-sentence: trimmed, final punctuation dropped. */
function fragment(text: string, locale: string): string {
  return lowerFirstWord(text.replace(END_PUNCTUATION, ""), locale);
}

/**
 * The goal as one sentence, built from whichever parts are filled in. Null until the
 * Specific part — the action itself — exists, since every other clause hangs off it.
 */
export function smartStatement(
  input: SmartGoalInput,
  labels: SmartGoalLabels,
  locale: string
): string | null {
  const action = fragment(input.specific, locale);
  if (action === "") {
    return null;
  }
  const date = formatDeadline(input.deadline, locale);
  const measure = fragment(input.measurable, locale);
  const reason = fragment(input.relevant, locale);
  let sentence = t(labels.statement.will, { action });
  if (date) {
    sentence = `${t(labels.statement.by, { date })} ${sentence}`;
  }
  if (measure) {
    sentence = `${sentence}, ${t(labels.statement.measured, { measure })}`;
  }
  if (reason) {
    sentence = `${sentence}, ${t(labels.statement.because, { reason })}`;
  }
  return `${sentence}.`;
}

function answers(
  input: SmartGoalInput,
  locale: string
): Record<SmartCriterion, string> {
  return {
    specific: input.specific.trim(),
    measurable: input.measurable.trim(),
    achievable: input.achievable.trim(),
    relevant: input.relevant.trim(),
    timeBound: formatDeadline(input.deadline, locale) ?? "",
  };
}

export function buildSmartGoal(
  input: SmartGoalInput,
  labels: SmartGoalLabels,
  locale: string
): DocumentModel {
  const statement = smartStatement(input, labels, locale);
  const filled = answers(input, locale);
  const checklist: DocCheckItem[] = SMART_CRITERIA.map((criterion) => ({
    checked: filled[criterion] !== "",
    text: [
      { text: labels.criteria[criterion], bold: true },
      { text: ` — ${filled[criterion] || labels.missing[criterion]}` },
    ],
  }));
  const statementBlocks: DocBlock[] = statement
    ? [
        { kind: "paragraph", text: [{ text: statement, bold: true }] },
        { kind: "guidance", text: labels.statementNote },
      ]
    : [{ kind: "prompt", text: labels.statementPrompt }];
  const goal = input.goal.trim();
  return {
    title: labels.defaultTitle,
    subtitle: goal ? t(labels.startingPoint, { goal }) : undefined,
    sections: [
      { heading: labels.headings.statement, blocks: statementBlocks },
      {
        heading: labels.headings.checklist,
        blocks: [{ kind: "checklist", items: checklist }],
      },
      {
        heading: labels.headings.plan,
        blocks: [
          ...listOrPrompt(input.steps, "decimal", labels.stepsPrompt),
          {
            kind: "guidance",
            text: t(labels.checkIn, {
              frequency: labels.frequencies[input.checkIn],
            }),
          },
        ],
      },
    ],
  };
}
